import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Tag, Plus, Sparkles, Trash2, Copy, Calendar, Users, Gift, X,
  CheckCircle2, Clock, TrendingUp, BarChart3, MapPin, DollarSign,
} from 'lucide-react';

/**
 * Admin-facing promo-code manager.
 *
 * - Lists all promos scoped to the admin's tenant (or platform-wide for main admin).
 * - Single-code create (existing behaviour).
 * - Bulk campaign generator — admin sets reward, duration, per-user cap, and
 *   how many unique codes to mint. Codes come pre-generated with a custom
 *   prefix (e.g. SUMMER-X7KLZP) ready to hand out via email/SMS/flyers.
 * - Copy-to-clipboard for each code, delete, and a tiny live KPI strip.
 */
export default function PromoCodesPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/promotions');
      setPromos(res.data?.promotions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
    const t = setInterval(fetchPromos, 30000); // silent refresh every 30s
    return () => clearInterval(t);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code? Existing redemptions are kept, but no one new can use it.')) return;
    try {
      await api.delete(`/api/admin/promotions/${id}`);
      toast.success('Promo deleted');
      fetchPromos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const kpis = useMemo(() => {
    const active = promos.filter(p => p.enabled !== false).length;
    const totalClaims = promos.reduce((s, p) => s + (p.claim_count || p.times_used || 0), 0);
    const totalReward = promos.reduce((s, p) => s + (p.reward_amount || 0) * (p.max_claims || 0), 0);
    return { total: promos.length, active, totalClaims, totalReward };
  }, [promos]);

  return (
    <div className="p-6 space-y-6" data-testid="promo-codes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="w-6 h-6 text-[#D4AF37]" /> Promo Codes
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Reward campaigns, retention codes, VIP drops — create single codes or mint a batch.
          </p>
        </div>
        <Button
          onClick={() => setShowBulk(true)}
          className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold"
          data-testid="promo-generate-bulk"
        >
          <Sparkles className="w-4 h-4 mr-2" /> Bulk Generate Campaign
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={<Tag className="w-4 h-4" />} label="Total Codes" value={kpis.total} />
        <KPI icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} label="Active" value={kpis.active} />
        <KPI icon={<TrendingUp className="w-4 h-4 text-[#D4AF37]" />} label="Claims" value={kpis.totalClaims} />
        <KPI icon={<Gift className="w-4 h-4 text-fuchsia-300" />} label="Max FP Liability" value={kpis.totalReward.toLocaleString()} />
      </div>

      {/* Codes list */}
      <div className="bg-[#131822] border border-white/5 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 text-[10px] uppercase text-gray-500 px-4 py-3 border-b border-white/5">
          <div className="col-span-3">Code</div>
          <div className="col-span-3">Campaign</div>
          <div className="col-span-2">Reward</div>
          <div className="col-span-2">Usage</div>
          <div className="col-span-1">Ends</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">Loading…</div>
        ) : promos.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500 text-sm">
            No promo codes yet. Click <span className="text-[#D4AF37]">Bulk Generate Campaign</span> to mint your first batch.
          </div>
        ) : (
          promos.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 text-sm hover:bg-white/5" data-testid={`promo-row-${p.code}`}>
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <code className="text-[#D4AF37] font-mono font-bold truncate">{p.code || '—'}</code>
                {p.code ? (
                  <button onClick={() => copyCode(p.code)} className="text-gray-500 hover:text-[#D4AF37]" title="Copy" data-testid={`promo-copy-${p.code}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                ) : null}
              </div>
              <div className="col-span-3 text-gray-300 truncate">{p.title || p.campaign_title || '—'}</div>
              <div className="col-span-2 text-white font-semibold">
                {(p.reward_amount || 0).toLocaleString()}
                <span className="text-[10px] text-gray-500 ml-1">
                  {p.type === 'deposit_bonus' ? '% · deposit match' : 'FP · free play'}
                </span>
              </div>
              <div className="col-span-2 text-gray-400 text-xs flex items-center gap-1">
                <Users className="w-3 h-3" />
                {(p.claim_count || p.times_used || 0)} / {(p.max_claims || '∞')}
              </div>
              <div className="col-span-1 text-gray-500 text-[11px]">
                {p.end_date ? new Date(p.end_date).toLocaleDateString() : '—'}
              </div>
              <div className="col-span-1 text-right flex items-center justify-end gap-1">
                <button onClick={() => setAnalyticsFor(p)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Analytics" data-testid={`promo-analytics-${p.code}`}>
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="text-gray-500 hover:text-red-400 p-1" title="Delete" data-testid={`promo-delete-${p.code}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showBulk && (
        <BulkGenerateModal
          onClose={() => setShowBulk(false)}
          onGenerated={(res) => {
            setGenerated(res);
            setShowBulk(false);
            fetchPromos();
          }}
        />
      )}

      {generated && (
        <GeneratedCodesModal
          result={generated}
          onClose={() => setGenerated(null)}
        />
      )}

      {analyticsFor && (
        <PromoAnalyticsModal
          promo={analyticsFor}
          onClose={() => setAnalyticsFor(null)}
        />
      )}
    </div>
  );
}

function KPI({ icon, label, value }) {
  return (
    <div className="bg-[#131822] border border-white/5 rounded-xl p-3">
      <div className="text-[10px] text-gray-500 uppercase flex items-center gap-1">{icon} {label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}

function BulkGenerateModal({ onClose, onGenerated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    reward_amount: 50,
    reward_type: 'bonus',
    duration_days: 30,
    count: 10,
    max_per_user: 1,
    prefix: '',
    code_length: 8,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Campaign title required'); return; }
    if (form.count < 1 || form.count > 500) { toast.error('Count must be 1–500'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/admin/promotions/bulk-generate', {
        ...form,
        reward_amount: Number(form.reward_amount) || 0,
        duration_days: Number(form.duration_days) || 30,
        count: Number(form.count) || 1,
        max_per_user: Number(form.max_per_user) || 1,
        code_length: Number(form.code_length) || 8,
      });
      toast.success(`Generated ${res.data?.generated || 0} codes`);
      onGenerated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-[#131822] border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#131822] border-b border-white/5 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> <span className="font-bold text-white">New Campaign</span></div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Campaign Title *">
            <Input value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="Summer 2026 Kickoff" data-testid="bulk-title" />
          </Field>
          <Field label="Description (shown to player on redeem)">
            <Input value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Free 50 FP to play — limited time!" data-testid="bulk-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reward Amount (FP)">
              <Input type="number" value={form.reward_amount} onChange={(e) => update('reward_amount', e.target.value)} data-testid="bulk-reward-amount" />
            </Field>
            <Field label="Reward Type (FP only)">
              <select value={form.reward_type} onChange={(e) => update('reward_type', e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="bulk-reward-type">
                <option value="bonus">FP Credits (Free Play)</option>
                <option value="deposit_bonus">Deposit % Match as FP</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valid For (days)">
              <Input type="number" value={form.duration_days} onChange={(e) => update('duration_days', e.target.value)} data-testid="bulk-duration" />
            </Field>
            <Field label="Max Uses per Player">
              <Input type="number" value={form.max_per_user} onChange={(e) => update('max_per_user', e.target.value)} data-testid="bulk-max-per-user" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="How Many Codes?">
              <Input type="number" value={form.count} onChange={(e) => update('count', e.target.value)} data-testid="bulk-count" />
            </Field>
            <Field label="Code Prefix">
              <Input value={form.prefix} onChange={(e) => update('prefix', e.target.value.toUpperCase())} placeholder="SUMMER" data-testid="bulk-prefix" />
            </Field>
            <Field label="Code Length">
              <Input type="number" value={form.code_length} onChange={(e) => update('code_length', e.target.value)} data-testid="bulk-code-length" />
            </Field>
          </div>
          <div className="text-[11px] text-gray-500 bg-[#0B0E14] border border-white/5 rounded-lg p-3">
            Each player can redeem once. You'll see the list of codes right after — copy them, email them, print them on flyers, whatever fits your campaign.
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#131822] border-t border-white/5 px-5 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold" data-testid="bulk-submit">
            {saving ? 'Generating…' : `Generate ${form.count} codes`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function GeneratedCodesModal({ result, onClose }) {
  const codes = result?.codes || [];
  const copyAll = () => {
    navigator.clipboard?.writeText(codes.map((c) => c.code).join('\n'));
    toast.success(`Copied ${codes.length} codes`);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-[#131822] border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-md max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span className="font-bold text-white">{codes.length} codes ready</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1 font-mono text-sm">
          {codes.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-[#0B0E14] border border-white/5 rounded px-3 py-2">
              <span className="text-[#D4AF37]">{c.code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success('Copied'); }} className="text-gray-500 hover:text-[#D4AF37]">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-white/5 flex gap-2">
          <Button onClick={copyAll} className="flex-1 bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold" data-testid="generated-copy-all">
            <Copy className="w-4 h-4 mr-2" /> Copy all
          </Button>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

/**
 * Per-promo analytics drill-down.
 * Pulls /api/admin/promotions/:id/analytics which returns:
 *   - promo meta (code, reward, limits, dates)
 *   - stats: total_redemptions, total_sc_credited, post_redeem_depositors,
 *     post_redeem_deposit_total, first_time_depositors, deposit_conversion_rate
 *   - redemptions: [{user, amount_credited, redeemed_at, post_deposit?, country, tenant_id}]
 */
function PromoAnalyticsModal({ promo, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/admin/promotions/${promo.id}/analytics`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || 'Failed to load analytics');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [promo.id]);

  const stats = data?.stats;
  const redemptions = data?.redemptions || [];

  // Country breakdown
  const byCountry = {};
  redemptions.forEach((r) => {
    const k = r.country || 'Unknown';
    byCountry[k] = (byCountry[k] || 0) + 1;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-[#131822] border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="promo-analytics-modal">
        <div className="sticky top-0 bg-[#131822] border-b border-white/5 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <BarChart3 className="w-5 h-5 text-[#D4AF37] shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-white truncate">{promo.title || 'Campaign'}</div>
              <code className="text-[11px] text-[#D4AF37] font-mono">{promo.code}</code>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && (
            <div className="text-center text-gray-500 text-sm py-8">Loading analytics…</div>
          )}

          {stats && (
            <>
              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="analytics-stats">
                <MiniStat label="Redemptions" value={stats.total_redemptions} icon={<Users className="w-3.5 h-3.5 text-[#D4AF37]" />} />
                <MiniStat label="Total FP Credited" value={`${(stats.total_sc_credited || 0).toLocaleString()} FP`} icon={<Gift className="w-3.5 h-3.5 text-fuchsia-300" />} />
                <MiniStat label="Deposit Conversion" value={`${stats.deposit_conversion_rate}%`} icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-400" />} hint="of redeemers who deposited within 7 days" />
                <MiniStat label="Post-Redeem Depositors" value={stats.post_redeem_depositors} icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />} />
                <MiniStat label="First-Time Depositors" value={stats.first_time_depositors} icon={<Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />} hint="converted from promo" />
                <MiniStat label="Post-Redeem Deposit $" value={`$${(stats.post_redeem_deposit_total || 0).toLocaleString()}`} icon={<DollarSign className="w-3.5 h-3.5 text-emerald-400" />} />
              </div>

              {/* Country breakdown */}
              {Object.keys(byCountry).length > 0 && (
                <div>
                  <div className="text-xs uppercase text-gray-500 flex items-center gap-1 mb-2">
                    <MapPin className="w-3.5 h-3.5" /> By Country
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="analytics-countries">
                    {Object.entries(byCountry).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                      <span key={c} className="px-2 py-1 rounded-full text-xs bg-[#0B0E14] border border-white/10 text-gray-300">
                        {c} · <span className="text-[#D4AF37] font-semibold">{n}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Redemption list */}
              <div>
                <div className="text-xs uppercase text-gray-500 mb-2">Redemptions</div>
                <div className="border border-white/5 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-gray-500 px-3 py-2 border-b border-white/5">
                    <div className="col-span-4">Player</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-3">Redeemed</div>
                    <div className="col-span-3">Post-Deposit (7d)</div>
                  </div>
                  {redemptions.length === 0 ? (
                    <div className="px-3 py-8 text-center text-gray-500 text-sm">No redemptions yet.</div>
                  ) : (
                    redemptions.map((r, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 border-b border-white/5 text-[12px] hover:bg-white/5">
                        <div className="col-span-4 min-w-0">
                          <div className="text-white truncate">{r.full_name || r.username || '—'}</div>
                          <div className="text-[10px] text-gray-500 truncate">{r.email || '—'}</div>
                        </div>
                        <div className="col-span-2 text-[#D4AF37] font-semibold">
                          {(r.amount_credited || 0).toLocaleString()} <span className="text-[10px] text-gray-500">FP</span>
                        </div>
                        <div className="col-span-3 text-gray-400 text-[11px]">
                          {r.redeemed_at ? new Date(r.redeemed_at).toLocaleString() : '—'}
                        </div>
                        <div className="col-span-3 text-[11px]">
                          {r.post_deposit ? (
                            <span className="text-emerald-400 font-semibold">${(r.post_deposit.amount || 0).toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon, hint }) {
  return (
    <div className="bg-[#0B0E14] border border-white/5 rounded-xl p-3">
      <div className="text-[10px] uppercase text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      {hint ? <div className="text-[10px] text-gray-600 mt-1 leading-snug">{hint}</div> : null}
    </div>
  );
}
